import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useVoiceVisualizer } from 'react-voice-visualizer';
import { formatToHHMMSS } from '@/utils/function';

import { INIT_RECORD_DATA_STATE } from '@/recoil/init_data';
import { UploadStallError, useUploadFile } from '@/hooks/apiHooks';
import { getAudioDuration } from '@/components/Record/util/function';
import { captureRecordError } from '@/components/Record/util/sentry';
import { RecordDataState, Recording } from '@/types/api';
import { t } from 'i18next';
import { useDialog } from '@/hooks/global-tools';
import DraggableRecord from '@/components/InitializeRecorder/DraggableRecord';
import {
  cleanupOldRecords,
  deleteFromQueue,
  getQueuedRecordById,
  saveToQueue,
  uploadFailedDialogId,
} from '@/utils/audioUploadQueue';
import {
  holdLock,
  isLockHeld,
  LIVE_RECOVERY_LOCK,
  queueRecordLock,
  RECORDING_LOCK,
  runWithLock,
} from '@/utils/browserLock';
import {
  appendLiveChunk,
  discardLiveSession,
  LIVE_CHUNK_MS,
  liveSessionLock,
  newLiveSessionId,
  recoverInterruptedSessions,
  startLiveSession,
} from '@/utils/audioLiveBackup';
import { getInvalidRecordingReason } from '@/utils/recordingIntegrity';
import { notifyAudioQueueChanged } from '@/utils/audioUploadQueue';
import { getFormattedToday } from '@/utils/format';
import {
  getAudioExtensionFromMimeType,
  getSupportedRecordingMimeType,
  isRecordingSupported,
  withAudioExtension,
} from '@/utils/audioFormat';
import { uploadWithRetry } from '@/utils/audioUploadRetry';
import { Controls } from '../Record/VoiceRecord2/CustomVoiceVisualizer';

// ── 무음 감지 파라미터 ──────────────────────────────────────────────
// 녹음 시작 후 이 창(실제 녹음 시간 기준, 일시정지 제외) 동안 소리가 한 번도
// 없으면 최초 1회 경고. 창 안에 소리가 한 번이라도 있으면 그 세션은 이후 판정 안 함.
const SILENCE_WINDOW_MS = 15000; // 초기 무음 판정 창(15초)
const SILENCE_PEAK_THRESHOLD = 6; // |sample-128| 피크 기준(0~127), 낮출수록 민감
const SILENCE_SAMPLE_MS = 300; // 볼륨 샘플링 주기

// ── 마이크 예외 안내 (PORTAL-12591) ──────────────────────────────────
// getUserMedia 실패 원인은 DOMException.name 에 담긴다. react-voice-visualizer 는
// 원본 에러를 그대로 controls.error 로 넘기므로(DOMException 은 Error 를 상속)
// name 으로 원인을 갈라낼 수 있다. 원인마다 사용자가 취할 조치가 달라서 문구를 나눈다.
const getMicErrorMessage = (error: Error): string => {
  switch (error.name) {
    case 'NotAllowedError': // 사용자가 권한 거부했거나 브라우저 정책이 차단
      return t(
        '마이크 권한이 없어 녹음할 수 없어요.\n브라우저 설정에서 마이크 사용을 허용한 뒤 다시 시도해 주세요.',
      );
    case 'NotFoundError': // 연결된 입력 장치가 없음
      return t(
        '연결된 마이크를 찾을 수 없어요.\n마이크가 연결되어 있는지 확인해 주세요.',
      );
    case 'NotReadableError': // 장치는 있으나 OS/다른 앱이 점유해 열 수 없음
      return t(
        '다른 앱에서 마이크를 사용 중이에요.\n해당 앱을 종료한 뒤 다시 시도해 주세요.',
      );
    default:
      return t(
        '마이크를 사용할 수 없어요.\n마이크 연결과 브라우저 권한을 확인해 주세요.',
      );
  }
};

// 시간영역 파형(Uint8Array, 무음 ≈ 128)에서 피크 진폭 계산
const getPeak = (data?: Uint8Array): number => {
  if (!data || data.length === 0) return 0;
  let peak = 0;
  for (let i = 0; i < data.length; i++) {
    const d = Math.abs(data[i] - 128);
    if (d > peak) peak = d;
  }
  return peak;
};

const RecorderContext = createContext<RecorderContextValue | null>(null);

type RecorderContextValue = {
  controls: Controls;
  recordedBlob: Blob | null;
  isRecordingInProgress: boolean;
  recordingTime: number;
  isPausedRecording: boolean;
  /** 녹음 시작 — 다른 탭이 녹음 중이거나 지원하지 않아 시작하지 못하면 false */
  start: (docId: string) => Promise<boolean>;
  pauseResume: () => void;
  stop: () => void;
  state: RecordDataState;
  /**
   * 녹음 상태를 비운다 — 지금 녹음 상태가 uuid 의 녹음일 때만.
   * 늦게 끝난 이전 녹음의 연결·포기 처리가 그사이 시작된 새 녹음 상태를 지우지 않게 한다.
   */
  setStateInit: (uuid: string) => void;
  draggable: (flag: boolean) => void;
};

export const RecorderProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // 라이브러리 기본 이탈 경고는 녹음 중만 막아서 업로드 구간이 비므로, 아래에서 직접 건다.
  const controls = useVoiceVisualizer({ shouldHandleBeforeUnload: false });
  const {
    recordedBlob,
    isRecordingInProgress,
    recordingTime,
    isPausedRecording,
  } = controls;

  const dialog = useDialog();

  const [state, setState] = useState(INIT_RECORD_DATA_STATE);

  // 최신 controls 를 인터벌 내부에서 읽기 위한 ref (audioData 는 프레임마다 갱신됨)
  const controlsRef = useRef(controls);
  controlsRef.current = controls;

  // ── 앱 진입 시 백업 정리 (PORTAL-12583 · 12587) ────────────────────
  // 비정상 종료로 남은 녹음을 백업 큐로 옮기고, 보관 기간이 끝난 백업을 지운다.
  // 삭제 예고(내려받기) 안내는 기획 요청으로 뺐다 — 미복구 녹음은 목록 라벨과 문서 화면에서 드러낸다.
  useEffect(() => {
    const tidyBackups = async () => {
      await recoverInterruptedRecordings();
      await cleanupOldRecords();
    };
    tidyBackups().catch(console.error);
  }, []);

  // ── 탭 간 녹음 잠금 (PORTAL-12586) ─────────────────────────────────
  // 녹음 중복 방지가 탭 로컬 상태뿐이라 다른 탭에서 동시에 녹음이 시작됐다.
  // 녹음하는 동안 탭 간 잠금을 쥐고, 녹음이 끝나거나 시작에 실패하면 놓는다.
  const recordingLockRef = useRef<(() => void) | null>(null);
  const releaseRecordingLock = () => {
    recordingLockRef.current?.();
    recordingLockRef.current = null;
  };

  // ── 진행 중 녹음 중간 저장 (PORTAL-12587) ────────────────────────────
  // 녹음 결과는 끝날 때까지 라이브러리 MediaRecorder 메모리에만 있어 크래시·새로고침이면 전부 잃는다.
  // 라이브러리 녹음은 그대로 두고, 같은 마이크 스트림에 백업용 MediaRecorder 를 하나 더 붙여
  // LIVE_CHUNK_MS 마다 조각을 IndexedDB 에 쌓는다. 라이브러리의 timeslice 옵션은 쓰면
  // 최종 recordedBlob 을 만들지 않고 마지막 조각도 놓쳐서 쓰지 않는다.
  // 정상 종료되면 전체 녹음이 업로드되거나 백업 큐로 넘어간 뒤 조각을 지운다.
  type LiveBackup = { sessionId: string; release: (() => void) | null };
  const liveRecorderRef = useRef<MediaRecorder | null>(null);
  const liveBackupRef = useRef<LiveBackup | null>(null);
  // 녹음이 끝나 전체 녹음 처리를 기다리는 세션 — 처리 뒤 지운다
  const finishedLiveBackupRef = useRef<LiveBackup | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    if (!isRecordingInProgress) return;
    const main = controlsRef.current.mediaRecorder;
    if (!main || typeof MediaRecorder === 'undefined') return;
    let ended = false;

    (async () => {
      const { docId, title } = stateRef.current;
      // 녹음하는 동안 쥐는 잠금 — 다른 탭이 이 세션을 비정상 종료로 오인해 가져가지 않게.
      // 세션을 기록하기 전에 먼저 쥔다: 기록 뒤 잠금 사이의 틈에 다른 탭의 되살리기가 끼지 않게
      const sessionId = newLiveSessionId();
      const release = await holdLock(liveSessionLock(sessionId));
      await startLiveSession(
        { docId, title, mimeType: main.mimeType },
        sessionId,
      );
      if (ended) {
        // 시작 준비 중에 녹음이 이미 끝났다 — 전체 녹음이 있으니 조각은 필요 없다
        await discardLiveSession(sessionId);
        release?.();
        return;
      }
      const recorder = new MediaRecorder(
        main.stream,
        main.mimeType ? { mimeType: main.mimeType } : undefined,
      );
      let seq = 0;
      let chunkFailureReported = false;
      recorder.addEventListener('dataavailable', (event) => {
        appendLiveChunk(sessionId, seq++, event.data).catch((e) => {
          // 조각을 못 쌓으면 비정상 종료 때 되살릴 게 없다 — 조용히 넘기지 않고 한 세션에 한 번 남긴다
          if (chunkFailureReported) return;
          chunkFailureReported = true;
          captureRecordError('record', e, {
            docId,
            title,
            mimeType: main.mimeType,
            reason: 'live-backup-chunk',
          });
        });
      });
      recorder.start(LIVE_CHUNK_MS);
      if (controlsRef.current.isPausedRecording) recorder.pause();
      liveRecorderRef.current = recorder;
      liveBackupRef.current = { sessionId, release };
    })().catch((e) => {
      // 중간 저장은 보조 장치다 — 실패해도 본 녹음은 그대로 진행한다
      captureRecordError('record', e, {
        docId: stateRef.current.docId,
        reason: 'live-backup',
      });
    });

    return () => {
      ended = true;
      const recorder = liveRecorderRef.current;
      if (recorder && recorder.state !== 'inactive') recorder.stop();
      liveRecorderRef.current = null;
      finishedLiveBackupRef.current = liveBackupRef.current;
      liveBackupRef.current = null;
    };
  }, [isRecordingInProgress]);

  // 일시정지 · 재개를 백업 녹음에도 똑같이 — 쉬는 구간이 조각에 섞이지 않게
  useEffect(() => {
    const recorder = liveRecorderRef.current;
    if (!recorder) return;
    if (isPausedRecording && recorder.state === 'recording') recorder.pause();
    if (!isPausedRecording && recorder.state === 'paused') recorder.resume();
  }, [isPausedRecording]);

  /** 전체 녹음 처리(업로드 성공 · 백업 큐 저장 · 무효 판정)가 끝난 세션의 조각을 지운다 */
  const finishLiveBackup = (live: LiveBackup | null) => {
    if (!live) return;
    discardLiveSession(live.sessionId)
      .catch(console.error)
      .finally(() => live.release?.());
  };

  /**
   * 지난번 비정상 종료로 남은 녹음을 백업 큐로 옮기고 알린다.
   * 탭 간 잠금 아래에서 한다 — 여러 탭이 동시에 열리면 같은 세션을 각자 큐에 쌓아 백업이 겹쳤다.
   */
  const recoverInterruptedRecordings = async () => {
    const result = await runWithLock(LIVE_RECOVERY_LOCK, () =>
      recoverInterruptedSessions({
        isAlive: (sessionId) => isLockHeld(liveSessionLock(sessionId)),
        getDuration: async (file) =>
          formatToHHMMSS(await getAudioDuration(file)),
        onInvalid: (session, reason, file) =>
          captureRecordError(
            'record',
            new Error(`invalid recovered recording: ${reason}`),
            {
              docId: session.docId,
              title: session.title,
              mimeType: file.type,
              blobSize: file.size,
              reason: 'live-backup-invalid',
            },
          ),
      }),
    );
    if (!result.ran || result.value === 0) return;
    const recovered = result.value;
    notifyAudioQueueChanged(); // 열려 있는 회의록이 바로 복구 안내를 띄우게
    dialog.show({
      id: 'record-live-recovered',
      message: t(
        '녹음 중에 창이 닫혀 저장하지 못한 녹음 {{n}}건을 되살렸어요.\n해당 회의록에서 다시 올릴 수 있어요.',
        { n: recovered },
      ),
      confirmButtonText: t('확인'),
    });
  };

  const recordStart = async (docId: string): Promise<boolean> => {
    // 브라우저가 녹음을 지원하지 않으면(구형 Safari/iOS 등) 안내 후 중단
    if (!isRecordingSupported()) {
      dialog.show({
        id: 'record-unsupported',
        message: t(
          '이 브라우저에서는 녹음을 지원하지 않아요.\n최신 브라우저(Chrome, Safari 등)에서 다시 시도해 주세요.',
        ),
        confirmButtonText: t('확인'),
      });
      return false;
    }

    if (!recordingLockRef.current) {
      const release = await holdLock(RECORDING_LOCK);
      if (!release) {
        dialog.show({
          id: 'record-other-tab',
          message: t(
            '다른 창에서 녹음 중이에요.\n그 창에서 녹음을 마친 뒤 다시 시도해 주세요.',
          ),
          confirmButtonText: t('확인'),
        });
        return false;
      }
      recordingLockRef.current = release;
    }

    // 실제 지원 포맷에 맞춰 확장자 결정 (Safari/iOS 는 webm 대신 mp4 로 녹음됨)
    const extension = getAudioExtensionFromMimeType(
      getSupportedRecordingMimeType(),
    );

    controls.startRecording();
    setState({
      ...INIT_RECORD_DATA_STATE,
      title: `${getFormattedToday('dateTime')}.${extension}`,
      docId: docId,
      openDraggable: false,
      isUploading: false,
    });
    return true;
  };

  // 녹음이 끝나면(정지) 잠금을 놓는다. 시작 실패(마이크 오류)는 아래 오류 effect 에서 놓는다
  const wasRecordingRef = useRef(false);
  useEffect(() => {
    if (wasRecordingRef.current && !isRecordingInProgress) {
      releaseRecordingLock();
    }
    wasRecordingRef.current = isRecordingInProgress;
  }, [isRecordingInProgress]);
  useEffect(() => releaseRecordingLock, []);

  useEffect(() => {
    // queueId — 이미 백업해 둔 녹음을 다시 올리는 경우 그 백업 레코드 id (PORTAL-12582).
    // 재시도가 또 실패해도 새로 쌓지 않고, 성공하면 이 백업만 지운다.
    const updateRecordData = async (blobData: Blob, queueId?: number) => {
      // 처음 처리할 때만 — 재시도(queueId)는 이미 백업 큐에 있어 중간 저장분이 없다
      const live = queueId === undefined ? finishedLiveBackupRef.current : null;
      if (queueId === undefined) finishedLiveBackupRef.current = null;
      // 백업을 다시 올릴 땐 탭 간 잠금을 잡고, 그사이 다른 탭이 먼저 올렸는지 본다 (PORTAL-12586).
      // 같은 문서를 연 다른 탭의 복구 안내에서도 이 백업을 올릴 수 있다.
      let releaseQueueLock: (() => void) | null = null;
      if (queueId !== undefined) {
        releaseQueueLock = await holdLock(queueRecordLock(queueId));
        const alreadyHandled =
          !releaseQueueLock || !(await getQueuedRecordById(queueId));
        if (alreadyHandled) {
          releaseQueueLock?.();
          dialog.show({
            id: 'record-handled-other-tab',
            message: t(
              '이 녹음은 이미 다시 올렸거나 다른 창에서 올리는 중이에요.\n회의록을 다시 열어 확인해 주세요.',
            ),
            confirmButtonText: t('확인'),
          });
          setState(INIT_RECORD_DATA_STATE);
          return;
        }
      }
      setState((prev) => ({ ...prev, isUploading: true, uploadProgress: 0 }));
      // 실제 녹음 결과의 MIME 타입 기준으로 파일 확장자를 보정
      // (시작 시점 예측과 다를 수 있어 최종 blob.type 을 신뢰)
      const fileName = blobData.type
        ? withAudioExtension(
            state.title,
            getAudioExtensionFromMimeType(blobData.type),
          )
        : state.title;
      const fileData = new File([blobData], fileName, {
        type: blobData.type,
      });
      const duration = await getAudioDuration(blobData);
      const formattedDuration = formatToHHMMSS(duration);

      // 업로드 전 무결성 가드 — 빈/손상 녹음은 올리지 않고 재녹음을 안내한다.
      // 업로드 자체가 실패한 게 아니므로 큐에 저장(재시도)하지 않는다: 다시 올려도 같은 결과다.
      const invalidReason = getInvalidRecordingReason(blobData, duration);
      if (invalidReason) {
        captureRecordError(
          'record',
          new Error(`invalid recording: ${invalidReason}`),
          {
            docId: state.docId,
            title: fileName,
            mimeType: blobData.type,
            blobSize: blobData.size,
            duration: formattedDuration,
          },
        );
        dialog.show({
          id: 'recording-invalid',
          message: t(
            '녹음된 내용이 없어 저장하지 못했어요.\n마이크 상태를 확인하고 다시 녹음해 주세요.',
          ),
          confirmButtonText: t('확인'),
        });
        releaseQueueLock?.();
        finishLiveBackup(live);
        setState(INIT_RECORD_DATA_STATE); // 실패한 녹음을 버리고 초기 상태로 (isUploading 도 해제)
        return;
      }

      let attempts = 1;
      try {
        // 일시적 실패는 백업 큐로 보내기 전에 몇 번 더 올려 본다 (PORTAL-12581)
        const uploadData = await uploadWithRetry(
          () =>
            useUploadFile(fileData, true, undefined, ({ percent }) =>
              setState((prev) => ({ ...prev, uploadProgress: percent })),
            ),
          {
            onRetry: (attempt) => {
              attempts = attempt + 1;
              setState((prev) => ({ ...prev, uploadProgress: 0 }));
            },
          },
        );
        const recordingData: Recording = {
          name: uploadData.data.fileName,
          uuid: uploadData.data.uuid,
          duration: formattedDuration,
          state: 'complete',
        };
        setState((prev) => ({ ...prev, recordingData: recordingData }));
        finishLiveBackup(live); // 서버에 올라갔다 — 중간 저장분은 필요 없다
        // 이 녹음의 백업만 지운다 — 같은 문서의 다른(이전) 백업은 남긴다
        if (queueId !== undefined) await deleteFromQueue(queueId);
      } catch (e) {
        const backupId =
          queueId ??
          (await saveToQueue(
            state.docId,
            fileName,
            fileData,
            formattedDuration,
          ));
        finishLiveBackup(live); // 전체 녹음이 백업 큐에 들어갔다
        // 「취소」 — 문서 화면이 온라인 복구로 이 백업을 올리는 중이면 지우지 않는다.
        // 그 업로드가 실패하면 백업이 유일한 원본인데, 지웠으면 녹음을 잃는다 (PORTAL-12584)
        const handleCancel = () => {
          runWithLock(queueRecordLock(backupId), () =>
            deleteFromQueue(backupId),
          ).catch(console.error);
        };

        dialog.show({
          // 문서 화면이 온라인 복구로 이 백업을 올리면 이 id 로 안내를 닫는다 (PORTAL-12584)
          id: uploadFailedDialogId(backupId),
          cancelButtonText: t('취소'),
          confirmButtonText: t('다시 시도'),
          // 멈춰서 끊은 경우는 네트워크를 확인하라고 안내한다 (일반 실패와 조치가 다름)
          message:
            e instanceof UploadStallError
              ? t(
                  '업로드가 멈춰서 중단했어요.\n네트워크 상태를 확인하고 다시 시도할까요?',
                )
              : t('파일 업로드에 실패했어요.\n다시 시도할까요?'),
          onConfirm: () => updateRecordData(blobData, backupId),
          onCancel: handleCancel,
          isBlockBackDropClose: true,
        });
        captureRecordError('upload', e, {
          docId: state.docId,
          title: state.title,
          mimeType: blobData.type,
          blobSize: blobData.size,
          duration: formattedDuration,
          attempts, // 자동 재시도까지 포함한 시도 횟수 (PORTAL-12581)
        });
      } finally {
        releaseQueueLock?.();
        setState((prev) => ({ ...prev, isUploading: false }));
      }
    };
    if (recordedBlob) {
      updateRecordData(recordedBlob);
    }
  }, [recordedBlob]);

  // ── 마이크 이상 감지 ────────────────────────────────────────────────
  // 1) 권한/장치 오류: getUserMedia 실패(권한 거부·마이크 없음·다른 앱 점유) 시
  //    controls.error 가 채워짐 → 안내 alert
  useEffect(() => {
    if (!controls.error) return;
    releaseRecordingLock(); // 시작에 실패했으면 다른 탭이 녹음할 수 있게 놓는다
    captureRecordError('record', controls.error, {
      docId: state.docId,
      title: state.title,
      reason: controls.error.name, // 권한 거부/기기 없음/점유 중 구분
    });
    dialog.show({
      id: 'mic-permission-error', // 같은 id 는 중복으로 쌓이지 않음
      message: getMicErrorMessage(controls.error),
      confirmButtonText: t('확인'),
    });
  }, [controls.error]);

  // 2) 무음 감지(초기 창 1회): 녹음 시작 후 실제 녹음 SILENCE_WINDOW_MS(15초) 동안
  //    임계 볼륨 이상의 소리가 한 번도 없으면 최초 1회만 경고.
  //    창 안에 소리가 한 번이라도 있으면 그 세션은 이후 판정하지 않는다.
  //    (마이크는 정상이나 소리가 안 들어오는 경우 — 음소거·입력 오설정 등 — 을 잡는다)
  useEffect(() => {
    if (!isRecordingInProgress) return;
    let activeMs = 0; // 실제 녹음(일시정지 제외) 누적 시간
    let heardSound = false; // 창 안에서 소리가 한 번이라도 들렸는지
    let evaluated = false; // 창 판정 완료(이후엔 검사 안 함)

    const timerId = setInterval(() => {
      if (evaluated) return;
      const c = controlsRef.current;
      // 일시정지·녹음중 아님 → 시간 적립 안 함(실제 녹음 15초 기준 유지)
      if (!c.isRecordingInProgress || c.isPausedRecording) return;

      activeMs += SILENCE_SAMPLE_MS;
      if (getPeak(c.audioData) >= SILENCE_PEAK_THRESHOLD) {
        heardSound = true;
      }

      if (activeMs >= SILENCE_WINDOW_MS) {
        evaluated = true;
        if (!heardSound) {
          dialog.show({
            id: 'mic-silence',
            message: t(
              '마이크에서 소리가 감지되지 않아요.\n마이크 음소거나 입력 장치를 확인해 주세요.',
            ),
            confirmButtonText: t('확인'),
          });
        }
      }
    }, SILENCE_SAMPLE_MS);

    return () => clearInterval(timerId);
  }, [isRecordingInProgress]);

  // ── 이탈 경고 (PORTAL-12580) ────────────────────────────────────────
  // 녹음 중뿐 아니라 업로드 중(정지 후 서버 전송 구간)에도 창 닫기·새로고침을 막는다.
  // 이 구간에서 이탈하면 메모리의 blob 이 그대로 사라진다(큐 저장은 업로드 실패 시에만 일어남).
  // 문구는 최신 브라우저가 무시하고 자체 문구를 띄우므로, returnValue 는 경고 발생 신호로만 쓴다.
  useEffect(() => {
    if (!isRecordingInProgress && !state.isUploading) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isRecordingInProgress, state.isUploading]);

  const setStateInit = (uuid: string) => {
    setState((prev) =>
      prev.recordingData?.uuid === uuid ? INIT_RECORD_DATA_STATE : prev,
    );
  };
  const draggable = (flag: boolean) => {
    setState((prev) => ({ ...prev, openDraggable: flag }));
  };

  const value: RecorderContextValue = {
    controls,
    recordedBlob: recordedBlob ?? null,
    isRecordingInProgress: isRecordingInProgress,
    recordingTime,
    isPausedRecording,
    start: recordStart,
    pauseResume: () => controls.togglePauseResume(),
    stop: () => controls.stopRecording(),
    state,
    setStateInit,
    draggable,
  };

  return (
    <RecorderContext.Provider value={value}>
      {children}
      <DraggableRecord />
    </RecorderContext.Provider>
  );
};

export const useRecorder = () => {
  const ctx = useContext(RecorderContext);
  if (!ctx) throw new Error('useRecorder must be used within RecorderProvider');
  return ctx;
};
